import { BlockData, ApiConfig, ApiHealthStatus, RateLimit } from '../types/bitcoin';

// API configurations for all Bitcoin data sources
export const API_CONFIGS: Record<string, ApiConfig> = {
  mempool: {
    name: 'mempool.space',
    baseUrl: 'https://mempool.space/api',
    rateLimit: { requests: 10, window: 1 },
    timeout: 8000,
  },
  blockchair: {
    name: 'Blockchair',
    baseUrl: 'https://api.blockchair.com/bitcoin',
    rateLimit: { requests: 30, window: 60 },
    timeout: 10000,
  },
  blockchain: {
    name: 'Blockchain.com',
    baseUrl: 'https://blockchain.info',
    rateLimit: { requests: 60, window: 60 },
    timeout: 8000,
  },
  coinMetrics: {
    name: 'Coin Metrics',
    baseUrl: 'https://community-api.coinmetrics.io/v4',
    rateLimit: { requests: 100, window: 60 },
    timeout: 10000,
  },
};

// Rate limiting tracker
class RateLimiter {
  private requests: Map<string, number[]> = new Map();

  canMakeRequest(apiName: string, config: RateLimit): boolean {
    const now = Date.now();
    const windowStart = now - (config.window * 1000);
    
    if (!this.requests.has(apiName)) {
      this.requests.set(apiName, []);
    }
    
    const apiRequests = this.requests.get(apiName)!;
    
    // Remove old requests outside the window
    const validRequests = apiRequests.filter(time => time > windowStart);
    this.requests.set(apiName, validRequests);
    
    return validRequests.length < config.requests;
  }

  recordRequest(apiName: string): void {
    if (!this.requests.has(apiName)) {
      this.requests.set(apiName, []);
    }
    this.requests.get(apiName)!.push(Date.now());
  }

  getRequestsRemaining(apiName: string, config: RateLimit): number {
    const now = Date.now();
    const windowStart = now - (config.window * 1000);
    const apiRequests = this.requests.get(apiName) || [];
    const validRequests = apiRequests.filter(time => time > windowStart);
    return Math.max(0, config.requests - validRequests.length);
  }
}

export class BitcoinApiClient {
  private rateLimiter = new RateLimiter();
  private wsConnection: WebSocket | null = null;
  private healthStatus: ApiHealthStatus = this.initHealthStatus();

  constructor() {
    this.initWebSocket();
  }

  private initHealthStatus(): ApiHealthStatus {
    return Object.keys(API_CONFIGS).reduce((status, key) => {
      status[key as keyof ApiHealthStatus] = {
        available: true,
        lastCheck: Date.now(),
      };
      return status;
    }, {} as ApiHealthStatus);
  }

  // WebSocket connection for real-time mempool.space data
  private initWebSocket(): void {
    try {
      this.wsConnection = new WebSocket('wss://mempool.space/api/v1/ws');
      
      this.wsConnection.onopen = () => {
        console.log('📡 WebSocket connected to mempool.space');
        // Subscribe to new blocks
        this.wsConnection?.send(JSON.stringify({ action: 'want', data: ['blocks'] }));
      };

      this.wsConnection.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.block) {
            this.handleNewBlock(message.block);
          }
        } catch (error) {
          console.error('WebSocket message parsing error:', error);
        }
      };

      this.wsConnection.onclose = () => {
        console.log('📡 WebSocket disconnected, attempting reconnect...');
        setTimeout(() => this.initWebSocket(), 5000);
      };

      this.wsConnection.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket initialization failed:', error);
    }
  }

  private handleNewBlock(blockData: any): void {
    console.log('🔗 New block received:', blockData.height);
    // Emit custom event for components to listen to
    window.dispatchEvent(new CustomEvent('newBlock', { detail: blockData }));
  }

  // Fetch block data from mempool.space
  async fetchMempoolBlock(height: number): Promise<BlockData | null> {
    const config = API_CONFIGS.mempool;
    
    if (!this.rateLimiter.canMakeRequest('mempool', config.rateLimit)) {
      throw new Error('Rate limit exceeded for mempool.space');
    }

    try {
      const response = await fetch(`${config.baseUrl}/block-height/${height}`, {
        signal: AbortSignal.timeout(config.timeout),
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blockHash = await response.text();
      const blockResponse = await fetch(`${config.baseUrl}/block/${blockHash}`);
      const blockData = await blockResponse.json();

      this.rateLimiter.recordRequest('mempool');
      this.updateHealthStatus('mempool', true);

      return {
        height: blockData.height,
        hash: blockData.id,
        timestamp: blockData.timestamp,
        medianTimePast: blockData.mediantime,
        difficulty: blockData.difficulty,
        source: 'mempool',
      };
    } catch (error) {
      this.updateHealthStatus('mempool', false, error.message);
      console.error('Mempool API error:', error);
      return null;
    }
  }

  // Fetch block data from Blockchair
  async fetchBlockchairBlock(height: number): Promise<BlockData | null> {
    const config = API_CONFIGS.blockchair;
    
    if (!this.rateLimiter.canMakeRequest('blockchair', config.rateLimit)) {
      throw new Error('Rate limit exceeded for Blockchair');
    }

    try {
      const response = await fetch(`${config.baseUrl}/dashboards/block/${height}`, {
        signal: AbortSignal.timeout(config.timeout),
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      // Check if block data exists
      if (!data.data || Object.keys(data.data).length === 0) {
        console.warn(`Block ${height} not found in Blockchair`);
        this.updateHealthStatus('blockchair', true, `Block ${height} not yet indexed`);
        return null;
      }
      
      const blockData = data.data[height]?.block;
      if (!blockData) {
        console.warn(`Block ${height} data structure unexpected in Blockchair`);
        this.updateHealthStatus('blockchair', true, `Block ${height} data unavailable`);
        return null;
      }

      this.rateLimiter.recordRequest('blockchair');
      this.updateHealthStatus('blockchair', true);

      return {
        height: blockData.id,
        hash: blockData.hash,
        timestamp: new Date(blockData.time).getTime() / 1000,
        difficulty: blockData.difficulty,
        source: 'blockchair',
      };
    } catch (error) {
      this.updateHealthStatus('blockchair', false, error.message);
      console.error('Blockchair API error:', error);
      return null;
    }
  }

  // Fetch block data from Blockchain.com
  async fetchBlockchainBlock(height: number): Promise<BlockData | null> {
    const config = API_CONFIGS.blockchain;
    
    if (!this.rateLimiter.canMakeRequest('blockchain', config.rateLimit)) {
      throw new Error('Rate limit exceeded for Blockchain.com');
    }

    try {
      const response = await fetch(`${config.baseUrl}/block-height/${height}?format=json`, {
        signal: AbortSignal.timeout(config.timeout),
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      // Check if blocks array exists and has data
      if (!data.blocks || data.blocks.length === 0) {
        console.warn(`Block ${height} not found in Blockchain.com`);
        this.updateHealthStatus('blockchain', true, `Block ${height} not yet indexed`);
        return null;
      }
      
      const blockData = data.blocks[0];
      if (!blockData) {
        console.warn(`Block ${height} data unavailable in Blockchain.com`);
        this.updateHealthStatus('blockchain', true, `Block ${height} data unavailable`);
        return null;
      }

      this.rateLimiter.recordRequest('blockchain');
      this.updateHealthStatus('blockchain', true);

      return {
        height: blockData.height,
        hash: blockData.hash,
        timestamp: blockData.time,
        source: 'blockchain',
      };
    } catch (error) {
      this.updateHealthStatus('blockchain', false, error.message);
      console.error('Blockchain.com API error:', error);
      return null;
    }
  }

  // Get all available block data from multiple sources
  async fetchBlockFromAllSources(height: number): Promise<BlockData[]> {
    console.log(`🔍 Fetching block ${height} from all sources...`);
    
    const promises = [
      this.fetchMempoolBlock(height),
      this.fetchBlockchairBlock(height),
      this.fetchBlockchainBlock(height),
    ];

    const results = await Promise.allSettled(promises);
    const validBlocks = results
      .filter((result): result is PromiseFulfilledResult<BlockData> => 
        result.status === 'fulfilled' && result.value !== null
      )
      .map(result => result.value);

    console.log(`✅ Retrieved ${validBlocks.length} valid block sources for height ${height}`);
    return validBlocks;
  }

  // Get current API health status
  getHealthStatus(): ApiHealthStatus {
    return { ...this.healthStatus };
  }

  // Get rate limit status for all APIs
  getRateLimitStatus(): Record<string, number> {
    return Object.entries(API_CONFIGS).reduce((status, [key, config]) => {
      status[key] = this.rateLimiter.getRequestsRemaining(key, config.rateLimit);
      return status;
    }, {} as Record<string, number>);
  }

  private updateHealthStatus(apiName: string, available: boolean, error?: string): void {
    if (this.healthStatus[apiName as keyof ApiHealthStatus]) {
      this.healthStatus[apiName as keyof ApiHealthStatus] = {
        available,
        lastCheck: Date.now(),
        error,
      };
    }
  }

  // Clean up WebSocket connection
  disconnect(): void {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
  }
}

// Export singleton instance
export const bitcoinApi = new BitcoinApiClient();